package rendering

import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.composed
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.dp
import game.GravitarGame
import kotlin.math.abs
import kotlin.time.TimeSource

// ── Touch key IDs (match guidelineBindings) ───────────────────────────────────

private val TOUCH_MOVE_LEFT  = androidx.compose.ui.input.key.Key.DirectionLeft.keyCode.toString()
private val TOUCH_MOVE_RIGHT = androidx.compose.ui.input.key.Key.DirectionRight.keyCode.toString()
private val TOUCH_SOFT_DROP  = androidx.compose.ui.input.key.Key.DirectionDown.keyCode.toString()
private val TOUCH_HARD_DROP  = androidx.compose.ui.input.key.Key.Spacebar.keyCode.toString()
private val TOUCH_HOLD       = androidx.compose.ui.input.key.Key.C.keyCode.toString()
private val TOUCH_ROTATE_CCW = androidx.compose.ui.input.key.Key.Z.keyCode.toString()
private val TOUCH_ROTATE_CW  = androidx.compose.ui.input.key.Key.X.keyCode.toString()

/**
 * Adds touch/swipe gesture controls to the game board area.
 *
 * Gesture → action mapping:
 * - Swipe left/right and hold → move piece + DAS/ARR auto-repeat while held
 * - Swipe down fast           → hard drop
 * - Swipe up                  → hold piece
 * - Drag down slowly          → soft drop (held while dragging)
 * - Tap left half             → rotate CCW
 * - Tap right half            → rotate CW
 *
 * Horizontal movement uses the engine's built-in DAS/ARR: the key is sent as
 * held (no [GravitarGame.onKeyUp] until the finger lifts), so the engine's
 * existing auto-repeat timing applies exactly as with a keyboard.
 *
 * @param game    The active [GravitarGame] to send input to.
 * @param enabled When false, no touch events are processed (e.g. overlay visible).
 */
fun Modifier.touchGameInput(
    game: GravitarGame,
    enabled: Boolean = true,
): Modifier = composed {
    val density = LocalDensity.current

    val swipeHorizPx = with(density) { 18.dp.toPx() }
    val swipeDownPx  = with(density) { 40.dp.toPx() }
    val swipeUpPx    = with(density) { 30.dp.toPx() }
    val softDropPx   = with(density) { 8.dp.toPx() }
    val tapMaxMovePx = with(density) { 20.dp.toPx() }
    val tapMaxMs     = 400L

    // Keys currently held down by touch — released on finger lift
    var heldMoveKey  by remember { mutableStateOf<String?>(null) }
    var softDropActive by remember { mutableStateOf(false) }

    this.pointerInput(game, enabled) {
        if (!enabled) return@pointerInput

        val timeSource = TimeSource.Monotonic

        awaitEachGesture {
            val down = awaitFirstDown(requireUnconsumed = false)
            val startPos = down.position
            val startMark = timeSource.markNow()
            var lastPos = startPos
            var gestureHandled = false

            do {
                val event = awaitPointerEvent()
                val change = event.changes.firstOrNull() ?: break

                if (!change.pressed) break

                val currentPos = change.position
                val dx = currentPos.x - startPos.x
                val dy = currentPos.y - startPos.y
                val adx = abs(dx)
                val ady = abs(dy)

                if (!gestureHandled) {
                    when {
                        // ── Horizontal swipe → move left/right (held for DAS/ARR) ──────────
                        adx > swipeHorizPx && adx > ady * 1.5f -> {
                            gestureHandled = true
                            val key = if (dx < 0) TOUCH_MOVE_LEFT else TOUCH_MOVE_RIGHT

                            // If already holding the opposite direction, release it first
                            if (heldMoveKey != null && heldMoveKey != key) {
                                game.onKeyUp(heldMoveKey!!)
                                heldMoveKey = null
                            }

                            // Press and hold — do NOT call onKeyUp here.
                            // The engine's DAS/ARR will auto-repeat while the key stays held.
                            if (heldMoveKey == null) {
                                heldMoveKey = key
                                game.onKeyDown(key, isFresh = true)
                            }
                            change.consume()
                        }

                        // ── Swipe down fast → hard drop ───────────────────────────────────
                        dy > swipeDownPx && ady > adx * 1.5f -> {
                            gestureHandled = true
                            if (softDropActive) {
                                game.onKeyUp(TOUCH_SOFT_DROP)
                                softDropActive = false
                            }
                            game.onKeyDown(TOUCH_HARD_DROP, isFresh = true)
                            game.onKeyUp(TOUCH_HARD_DROP)
                            change.consume()
                        }

                        // ── Swipe up → hold piece ─────────────────────────────────────────
                        dy < -swipeUpPx && ady > adx * 1.5f -> {
                            gestureHandled = true
                            game.onKeyDown(TOUCH_HOLD, isFresh = true)
                            game.onKeyUp(TOUCH_HOLD)
                            change.consume()
                        }

                        // ── Slow downward drag → soft drop (held) ─────────────────────────
                        dy > softDropPx && ady > adx * 2f && !softDropActive -> {
                            // Only activate soft drop if the gesture has been going for a bit
                            // (avoids misclassifying a quick tap with slight downward drift)
                            val elapsed = startMark.elapsedNow().inWholeMilliseconds
                            if (elapsed > 80L) {
                                gestureHandled = true
                                softDropActive = true
                                game.onKeyDown(TOUCH_SOFT_DROP, isFresh = true)
                                change.consume()
                            }
                        }
                    }
                } else if (heldMoveKey != null) {
                    // Gesture already classified as horizontal move.
                    // If the finger crosses back past the start X, switch direction.
                    val newKey = if (dx < 0) TOUCH_MOVE_LEFT else TOUCH_MOVE_RIGHT
                    if (newKey != heldMoveKey && adx > swipeHorizPx) {
                        game.onKeyUp(heldMoveKey!!)
                        heldMoveKey = newKey
                        game.onKeyDown(newKey, isFresh = true)
                        change.consume()
                    }
                }

                lastPos = currentPos
            } while (true)

            // ── Finger lifted — release all held keys ─────────────────────────────
            heldMoveKey?.let { game.onKeyUp(it) }
            heldMoveKey = null

            if (softDropActive) {
                game.onKeyUp(TOUCH_SOFT_DROP)
                softDropActive = false
            }

            // ── Tap detection ─────────────────────────────────────────────────────
            if (!gestureHandled) {
                val elapsedMs = startMark.elapsedNow().inWholeMilliseconds
                val totalMove = abs(lastPos.x - startPos.x) + abs(lastPos.y - startPos.y)
                if (elapsedMs < tapMaxMs && totalMove < tapMaxMovePx) {
                    val screenWidth = size.width
                    if (startPos.x < screenWidth / 2f) {
                        game.onKeyDown(TOUCH_ROTATE_CCW, isFresh = true)
                        game.onKeyUp(TOUCH_ROTATE_CCW)
                    } else {
                        game.onKeyDown(TOUCH_ROTATE_CW, isFresh = true)
                        game.onKeyUp(TOUCH_ROTATE_CW)
                    }
                }
            }
        }
    }
}
